import {
  DataTableDataSearchDto,
  DatatableDataUserGroupSelectionDto,
  FooterDataDto,
  TableConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';
import merge from 'lodash/merge';

import {
  fetchFooterData,
  FetchFooterDataQueryKey,
} from '../../api/portalGateway/footerData';
import { prependRoleId } from '../../utils/prependRoleId';

type FooterData = {
  isLoading: boolean;
  data?: FooterDataDto;
};

export type UseFooterParams = {
  widgetId: string;
  tableConfig: TableConfigDto;
  /** Same page-filter selection object used for the table data request. */
  pageFilter: DataTableDataSearchDto['selection'];
  /** Same table-filter selection object used for the table data request. */
  tableFilter: DataTableDataSearchDto['selection'];
  titleColumnKey: string | undefined;
  shouldRequestFooterData: boolean;
  userGroupSelection: DatatableDataUserGroupSelectionDto['userGroupSelection'];
  isGroupedTable: boolean;
  stakeAdjusted?: boolean;
  groupByColumnId?: string;
};

export function useFooter({
  widgetId,
  tableConfig,
  pageFilter,
  tableFilter,
  titleColumnKey,
  shouldRequestFooterData,
  userGroupSelection,
  isGroupedTable,
  stakeAdjusted,
  groupByColumnId,
}: UseFooterParams): FooterData {
  const useGroupOnlyMode = groupByColumnId != null;
  const footerEnabled =
    (tableConfig.footer != null || isGroupedTable) && shouldRequestFooterData;

  const { data: allFooterData, isPending: isPendingAllFooter } = useQuery({
    queryKey: prependRoleId<FetchFooterDataQueryKey>([
      'footerDataAll',
      widgetId,
      pageFilter,
      tableFilter,
      'ALL',
      userGroupSelection,
      stakeAdjusted,
      groupByColumnId,
    ]),
    queryFn: fetchFooterData,
    enabled: footerEnabled && useGroupOnlyMode,
  });

  const { data: kpiFooterData, isPending: isPendingKpiFooter } = useQuery({
    queryKey: prependRoleId<FetchFooterDataQueryKey>([
      'footerDataKpi',
      widgetId,
      pageFilter,
      tableFilter,
      'KPI_ONLY',
      userGroupSelection,
      stakeAdjusted,
      groupByColumnId,
    ]),
    queryFn: fetchFooterData,
    enabled: footerEnabled && !useGroupOnlyMode,
  });

  const { data: nonKpiFooterData, isPending: isPendingNonKpiFooter } = useQuery(
    {
      queryKey: prependRoleId<FetchFooterDataQueryKey>([
        'footerDataNonKpi',
        widgetId,
        pageFilter,
        tableFilter,
        'NON_KPI',
        userGroupSelection,
        stakeAdjusted,
        groupByColumnId,
      ]),
      queryFn: fetchFooterData,
      enabled: footerEnabled && !useGroupOnlyMode,
    }
  );

  const footerTitle = tableConfig.footer?.footerTitle || 'Total';

  const data = useGroupOnlyMode
    ? allFooterData
    : kpiFooterData || nonKpiFooterData
      ? merge(kpiFooterData, nonKpiFooterData)
      : undefined;

  return {
    isLoading: useGroupOnlyMode
      ? isPendingAllFooter
      : isPendingKpiFooter || isPendingNonKpiFooter,
    data: {
      ...(tableConfig.footer != null &&
        titleColumnKey && {
          mainFooter: {
            ...data?.data.mainFooter,
            [titleColumnKey]: footerTitle,
          },
        }),
      ...(isGroupedTable && { groupFooter: data?.data.groupFooter }),
    },
  };
}
